import type { ReactNode } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { Loader2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { FormDialogHeader } from './FormDialogHeader';

const WIDTH_CLASS = {
  default: 'sm:max-w-lg',
  wide: 'sm:max-w-2xl',
} as const;

type FormDialogWidth = keyof typeof WIDTH_CLASS;

interface FormDialogProps<TValues extends FieldValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: LucideIcon;
  title: string;
  description: string;
  form: UseFormReturn<TValues>;
  isPending: boolean;
  isEditMode: boolean;
  onSubmit: (values: TValues) => void;
  width?: FormDialogWidth | undefined;
  children: ReactNode;
}

interface SubmitLabelProps {
  isPending: boolean;
  isEditMode: boolean;
}

function SubmitLabel({ isPending, isEditMode }: SubmitLabelProps) {
  if (isPending) {
    return (
      <>
        <Loader2 className="size-4 animate-spin" aria-hidden />
        Saving...
      </>
    );
  }
  return isEditMode ? 'Update' : 'Create';
}

export function FormDialog<TValues extends FieldValues>({
  open,
  onOpenChange,
  icon,
  title,
  description,
  form,
  isPending,
  isEditMode,
  onSubmit,
  width = 'default',
  children,
}: FormDialogProps<TValues>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-h-[calc(100dvh-2rem)] overflow-y-auto', WIDTH_CLASS[width])}
      >
        <FormDialogHeader icon={icon} title={title} description={description} />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            {children}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                <SubmitLabel isPending={isPending} isEditMode={isEditMode} />
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
